import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'specificai:commercialModelFavorites:v1';

/**
 * Stable id for a commercial model pick across providers
 * (same model id can exist under OpenAI and Azure).
 */
export const commercialFavoriteKey = (provider, model) =>
    `${provider || ''}::${model || ''}`;

const readFavorites = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(
            (entry) => entry && typeof entry.provider === 'string' && typeof entry.model === 'string'
        );
    } catch {
        return [];
    }
};

const writeFavorites = (next) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
        // Ignore quota / private-mode failures; UI still works for the session.
    }
};

/**
 * Client-persisted commercial-model favorites (localStorage).
 * No backend API exists yet for favorites — shared by every ModelSelection surface.
 */
export function useCommercialModelFavorites() {
    const [favorites, setFavorites] = useState(() => readFavorites());

    useEffect(() => {
        const onStorage = (event) => {
            if (event.key === STORAGE_KEY) {
                setFavorites(readFavorites());
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const isFavorite = useCallback(
        (provider, model) => {
            if (!provider || !model) return false;
            const key = commercialFavoriteKey(provider, model);
            return favorites.some(
                (entry) => commercialFavoriteKey(entry.provider, entry.model) === key
            );
        },
        [favorites]
    );

    const toggleFavorite = useCallback((provider, model) => {
        if (!provider || !model) return;
        setFavorites((prev) => {
            const key = commercialFavoriteKey(provider, model);
            const exists = prev.some(
                (entry) => commercialFavoriteKey(entry.provider, entry.model) === key
            );
            const next = exists
                ? prev.filter(
                    (entry) => commercialFavoriteKey(entry.provider, entry.model) !== key
                )
                : [...prev, { provider, model }];
            writeFavorites(next);
            return next;
        });
    }, []);

    return { favorites, isFavorite, toggleFavorite };
}

export default useCommercialModelFavorites;
